import { useCallback, useEffect, useMemo, useState } from 'react';
import { ZoneCreationProperties } from '../types/zoneMapType';
import { computeFixtureAllocations } from '../allocation/computeFixtureAllocations';
import { buildFixturePositionMap } from '../allocation/buildFixturePositionMap';
import { usePickPathDrawing } from './usePickPathDrawing';
import { FloorplanFixtureData } from '../zoneViewer/zoneMapDataType';
import logger from '@utils/logger';
import { useQueryClient } from '@tanstack/react-query';
import { useProgramConfig } from 'src/hooks/useProgramConfig';
import { useCustomNumbering } from './useCustomNumbering';
import { useFlowLabel } from '../utils/flowLabel';

interface UseSequenceDrawingInput {
  zoneCreationProperties: Map<number, ZoneCreationProperties>;
  activeZoneIdx: number;
  pickPathDrawing: ReturnType<typeof usePickPathDrawing>;
  fixtureOrderingMap: Map<string, number>;
  agileFixtures: FloorplanFixtureData[];
  store: string;
  setToasts: React.Dispatch<React.SetStateAction<unknown[]>>;
  updateFloorplanPickPath?: (input: { input: unknown }) => Promise<unknown>;
  floorplanId?: string;
}

export function useSequenceDrawing({
  zoneCreationProperties,
  activeZoneIdx,
  pickPathDrawing,
  fixtureOrderingMap,
  agileFixtures,
  store,
  setToasts,
  updateFloorplanPickPath,
  floorplanId,
}: UseSequenceDrawingInput) {
  const queryClient = useQueryClient();
  const config = useProgramConfig();
  const customPickPathNumbering = config.zoneMapBehavior.customPickPathNumbering;

  // Business-specific noun for the saved sequence (GFC: 'pick path', WFM/Fresh:
  // 'product flow'), in its cased forms.
  const flowLabel = useFlowLabel();

  // Start/step state, surfaced only when customPickPathNumbering is on (see
  // ZoneMapBehavior). Defaults to (1,1) so it's an identity transform otherwise.
  const { startNumber, stepIncrement, updateStartNumber, updateStepIncrement } = useCustomNumbering(1, 1);

  const editingFixtureOrderingMap = useMemo(() => {
    const map = new Map<string, number>();
    let seq = 1;
    for (const zone of zoneCreationProperties.values()) {
      for (const path of zone.pickPaths ?? []) {
        for (const wp of path.waypoints) {
          map.set(wp.fixtureId, seq++);
        }
      }
    }
    if (pickPathDrawing.inProgressPath) {
      for (const wp of pickPathDrawing.inProgressPath.waypoints) {
        map.set(wp.fixtureId, seq++);
      }
    }
    return map;
  }, [zoneCreationProperties, pickPathDrawing.inProgressPath]);

  // What the floorplan shows: the start/step labeling applied to the 1..N order
  // so rendered numbers match the saved pickPathId. editingFixtureOrderingMap
  // stays raw 1..N and feeds allocation math, untouched.
  const displayFixtureOrderingMap = useMemo(() => {
    if (!customPickPathNumbering) return editingFixtureOrderingMap;
    const map = new Map<string, number>();
    for (const [fixtureId, seq] of editingFixtureOrderingMap) {
      map.set(fixtureId, startNumber + (seq - 1) * stepIncrement);
    }
    return map;
  }, [customPickPathNumbering, editingFixtureOrderingMap, startNumber, stepIncrement]);

  const fixturePositionMap = useMemo(() => buildFixturePositionMap(agileFixtures), [agileFixtures]);

  const fixtureAssignments = useMemo(() => {
    const activeZone = zoneCreationProperties.get(activeZoneIdx);
    const allocations = (activeZone?.allocations ?? []).filter((a) => a.subcategoryId && a.percentage > 0);
    const orderingMap = editingFixtureOrderingMap.size > 0 ? editingFixtureOrderingMap : fixtureOrderingMap;
    if (allocations.length === 0 || !orderingMap || orderingMap.size === 0) return [];
    const fixtureSequence = Array.from(orderingMap.entries()).map(([fixtureId, sequenceOrder]) => ({
      fixtureId,
      sequenceOrder,
    }));
    return computeFixtureAllocations(allocations, fixtureSequence, fixturePositionMap);
  }, [zoneCreationProperties, activeZoneIdx, editingFixtureOrderingMap, fixtureOrderingMap, fixturePositionMap]);

  // Keyboard handlers for pick path drawing
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && pickPathDrawing.isDrawing) pickPathDrawing.stopDrawing();
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && pickPathDrawing.isDrawing) {
        e.preventDefault();
        pickPathDrawing.undoLastWaypoint();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pickPathDrawing]);

  useEffect(() => {
    if (!pickPathDrawing.isDrawing) return;
    window.addEventListener('pointerup', pickPathDrawing.onPointerUp);
    return () => window.removeEventListener('pointerup', pickPathDrawing.onPointerUp);
  }, [pickPathDrawing.isDrawing, pickPathDrawing.onPointerUp]);

  const [isSubmittingSequence, setIsSubmittingSequence] = useState(false);

  const handleSubmitSequence = useCallback(async () => {
    if (editingFixtureOrderingMap.size < agileFixtures.length) {
      setToasts((prev: unknown[]) => [
        ...prev,
        {
          id: `seq-partial-${Date.now()}`,
          children: `${flowLabel.sentence} must cover all ${agileFixtures.length} fixtures (currently ${editingFixtureOrderingMap.size}). Draw through all fixtures before saving.`,
          type: 'error' as const,
        },
      ]);
      return;
    }

    setIsSubmittingSequence(true);
    try {
      if (!updateFloorplanPickPath || !floorplanId) {
        setToasts((prev: unknown[]) => [
          ...prev,
          {
            id: `seq-no-floorplan-${Date.now()}`,
            children: `Cannot save ${flowLabel.lower}: no floorplan loaded.`,
            type: 'error' as const,
          },
        ]);
        setIsSubmittingSequence(false);
        return;
      }
      const fixtures = Array.from(displayFixtureOrderingMap.entries()).map(([fixtureId, pickPathId]) => ({
        fixtureId,
        pickPathId: String(pickPathId),
      }));
      await updateFloorplanPickPath({
        input: {
          storeId: store,
          floorplanId,
          fixtures,
        },
      });
      setToasts((prev: unknown[]) => [
        ...prev,
        {
          id: `seq-saved-${Date.now()}`,
          children: `${flowLabel.sentence} saved successfully.`,
          type: 'success' as const,
        },
      ]);

      pickPathDrawing.stopDrawing();
      void queryClient.invalidateQueries({ queryKey: ['GetFloorplan'] });
    } catch (err) {
      console.error(`Failed to save ${flowLabel.lower}:`, err);
      void logger.error(`Failed to save ${flowLabel.lower}`, err instanceof Error ? err : new Error(String(err)));
      setToasts((prev: unknown[]) => [
        ...prev,
        { id: `seq-error-${Date.now()}`, children: `Failed to save ${flowLabel.sentence}.`, type: 'error' as const },
      ]);
    } finally {
      setIsSubmittingSequence(false);
    }
  }, [
    editingFixtureOrderingMap,
    displayFixtureOrderingMap,
    updateFloorplanPickPath,
    store,
    floorplanId,
    setToasts,
    agileFixtures,
    queryClient,
    pickPathDrawing,
    flowLabel,
  ]);

  const hasPickPaths = useMemo(() => {
    for (const zone of zoneCreationProperties.values()) {
      if ((zone.pickPaths ?? []).length > 0) return true;
    }
    return false;
  }, [zoneCreationProperties]);

  return {
    editingFixtureOrderingMap,
    displayFixtureOrderingMap,
    fixtureAssignments,
    isSubmittingSequence,
    handleSubmitSequence,
    hasPickPaths,
    startNumber,
    stepIncrement,
    updateStartNumber,
    updateStepIncrement,
  };
}
